import React, { useMemo, useState } from 'react';
import { equals, prop, length, slice, find, propEq, pathOr, path, reduce, forEach, compose, defaultTo, head, split, all, pluck, filter, pick, propOr, map, any, assoc } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty, propNotEq } from 'ramda-adjunct';
import { filterDisplayedRows, filterUsers, transformUser } from '../components/users/utils/users';
import { isAdminRole, isSuperAdminUserRole, RoleLabelEnum, RoleValueEnum } from '../components/users/utils/roles';
import { EVEN_ROW_CLASSNAME, PAGE_SIZE } from '../_CONST';
import { ROLES, TEAM_ROLES } from '../lib/rights';
import usePagination from './usePagination';

const useUserTableData = (users, isAdministration, options, offers, defaultTeamId) => {
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  // format the list of users based on their roles and the administration context
  const teamsUsers = useMemo(
    () => reduce((acc, user) => {
      const shouldIncludeUser = isAdministration || !isSuperAdminUserRole(user);
      if (shouldIncludeUser) {
        const items = isAdministration ? offers : options;
        const formattedUser = transformUser(user, isAdministration, items);
        acc.push(formattedUser);
      }
      return acc;
    }, [])(users)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , [users, isAdministration, options]
  );

  // filter the users list
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredUsers = useMemo(() => filterUsers(teamsUsers, search, selectedItem, selectedRole, isAdministration), [teamsUsers, search, selectedItem, selectedRole]);
  
  const { pagination, onChangePagination } = usePagination({
    itemsNumber: length(filteredUsers),
    pageSizeKey: 'users',
    pageSize: PAGE_SIZE
  });
  
  const paginatedUsers = useMemo(() => {
    const { current, pageSize } = pagination;
    const start = (current - 1) * pageSize;
    const end = start + pageSize;
    return slice(start, end, filteredUsers);
  }, [filteredUsers, pagination]);

  const archivedOffers = useMemo(() => compose(
    map(pick(['id', 'offerName', 'team'])),
    filter(propEq(true, 'isArchived'))
  )(offers), [offers]);

  const usersDataSource = useMemo(() => compose(
    prop('data'),
    reduce((acc, user) => {
      const { email, teams, signUp, isSuperAdmin } = user;
      const { i, data } = acc;
      let index = 0;
      const rowClassName = i % 2 ? EVEN_ROW_CLASSNAME : '';
    
      const getRowData = ({ rowSpan = 1, item, roles, isArchived = false }) => ({
        rowSpan,
        index: index++,
        email,
        item,
        roles,
        signUp,
        user,
        rowClassName,
        isArchived
      });

      const items = isAdministration ? teams : path([0, 'roles'], teams);
      const rowSpan = length(items);
      const roles = pathOr([], [0, 'roles'], teams);
      const isAdmin = !isAdministration && isAdminRole(roles);
      const firstRole = prop('role', roles[0]);
      const allRoles = pluck('role', roles);
      const hasSameRoleOnAssignedOffers = all(equals(firstRole), allRoles);
      const isAssignedToAllOffers = equals(rowSpan, length(options));
      const hasSameRoleOnAllOffers = !isAdministration && hasSameRoleOnAssignedOffers && isAssignedToAllOffers;
      const teamRoles = pathOr([], [0, 'teamRoles'], teams);
      const displayedItems = filterDisplayedRows(items, {
        isAdministration, selectedItem, selectedRole 
      });

      if (isSuperAdmin) {
        const role = find(propEq(ROLES.superAdmin, 'value'))(TEAM_ROLES);
        data.push(getRowData({
          item: RoleLabelEnum.ALL_TEAMS,
          roles: [role] 
        }));
      } else if (isAdmin || hasSameRoleOnAllOffers) {
        const role = isAdministration ? roles[0] : teamRoles[0];
        data.push(getRowData({
          item: RoleLabelEnum.ALL_OFFERS,
          roles: [role] 
        }));
      } else if (isNotNilOrEmpty(items) && isNilOrEmpty(displayedItems)) {
        // The user has teams/offers, but none of them match the active filters: display nothing for this user.
      } else if (isNilOrEmpty(items)) {
        const label = isAdministration ? RoleLabelEnum.WITHOUT_TEAMS : RoleLabelEnum.WITHOUT_OFFERS;
        data.push(getRowData({
          item: label,
          roles: [{ label: RoleLabelEnum.WITHOUT_ROLES, value: RoleValueEnum.EMPTY }]
        }));
      } else {
        forEach((item) => {
          const currentTeamId = prop('teamId', item);
          const isOfferArchived = (currentOfferName) => any(({ team, offerName }) => equals(team, currentTeamId || defaultTeamId) && equals(offerName, currentOfferName), archivedOffers);
          const countArchivedOffers = compose(
            length,
            filter(isOfferArchived)
          );
          if (isAdministration) {
            const roles = compose(
              map((role) => {
                const offerNames = propOr([], 'offerNames', role);
                return assoc('archivedCount', countArchivedOffers(offerNames), role);
              }),
              prop('teamRoles')
            )(item);
            data.push(getRowData({
              rowSpan: length(displayedItems),
              item: prop('name', item),
              roles
            }));
          } else {
            const offerId = compose(
              head,
              split(','),
              defaultTo(''),
              prop('offerId')
            )(item);
            const offer = find(propEq(offerId, 'id'))(offers);
            const role = find(propEq(prop('role', item), 'value'), teamRoles);
            const offerName = prop('offerName', offer);
            data.push(getRowData({
              rowSpan: length(displayedItems),
              item: offerName,
              roles: [role],
              isArchived: isOfferArchived(offerName)
            }));
          }
        })(displayedItems);
      }

      acc.i += 1;
      return acc;
    }, { data:[], i: 0 })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  )(paginatedUsers), [paginatedUsers, archivedOffers, isAdministration, selectedItem, selectedRole]);

  const handleUpdate = (setter) => (value) => {
    if (propNotEq('current', 1, pagination)) {
      onChangePagination({ ...pagination, current: 1 }, { currentDataSource: usersDataSource });
    }
    setter(value);
  };

  const handleSearch = handleUpdate(setSearch);
  const handleSelectItem = handleUpdate(setSelectedItem);
  const handleSelectRole = handleUpdate(setSelectedRole);

  return {
    pagination,
    onChangePagination,
    search,
    setSearch: handleSearch,
    selectedItem,
    setSelectedItem: handleSelectItem,
    selectedRole,
    setSelectedRole: handleSelectRole,
    usersDataSource,
    filteredUsers,
    teamsUsers
  };
};

export default useUserTableData;